// Copy + formatting rules for the step timeline. Kept apart from the render
// tree so the "what do we call this step?" logic stays readable and testable.

import {
  contextualThinkingLabel,
  toolPastLabel,
  toolProgressLabel,
} from '../../../helpers/toolLabel';
import { workingStatusAt } from '../../../helpers/workingStatus';
import { STEP_CONTEXT_LOAD_FAILED } from '../../../constants';
import { pluralise } from '../../../helpers';
import { claudeThinkingStates } from '../../constants';
import type { TimelineStep } from './StepTimeline.types';

// Elapsed-time thresholds for the active row's copy. Hold the natural label
// until SOFTEN_AT, hand off to a calmer "still working" there, then a gentle
// nudge at WORRY_AT. Nothing is framed as an error while work is happening.
export const SOFTEN_AT_S = 20;
export const WORRY_AT_S = 60;
// Below this, the elapsed counter is hidden — a 1–2s blink reads as noise.
export const SHOW_ELAPSED_AT_S = 4;

// How long each thinking word holds before the next one takes over.
const ROTATE_EVERY_S = 6;

// The title's word for the moment. Keyed on elapsed time rather than random,
// so it's deterministic (a re-render can't churn it) and it always opens on
// "Thinking" — index 0 — before wandering off into the rest of the list.
const thinkingWordAt = (elapsedSec: number): string =>
  claudeThinkingStates[
    Math.floor(Math.max(0, elapsedSec) / ROTATE_EVERY_S) %
      claudeThinkingStates.length
  ] ?? 'Thinking';

// Which steps the user should see. Routing is pure plumbing (which agent
// picked up the turn) and never surfaces. Everything else does — including
// finished context/compose rows, because the whole point of the panel is to
// show the agent's reasoning, not just its tool calls.
// Routing is pure plumbing. Context/compose are too — surface them only while
// LIVE (an active row drives the streaming header) or on failure, never as a
// settled row. So a turn whose only steps were plumbing shows no card at all.
export const shouldShow = (s: TimelineStep): boolean => {
  if (s.id === 'action:routing') return false;
  // `remember` persists internal session context for later model turns. It is
  // bookkeeping, not user-requested work, so labels such as "Saved to memory"
  // must never leak into the live or settled activity timeline.
  if (s.name === 'remember') return false;
  if (s.id === 'action:context' || s.id.startsWith('action:compose')) {
    return s.status === 'active' || s.status === 'error';
  }
  return true;
};

// The right label for a tool row: prefer the description nexus/kula tools ship
// ("Searching: ruby developers") over the raw tool name ("kula_search"). The
// fallback is TENSE-AWARE — finished rows read past tense ("Searched Kula");
// only a live row keeps the in-flight verb.
const rowText = (s: TimelineStep): string => {
  if (s.description && s.description.trim().length > 0) return s.description;
  if (s.status === 'active') return toolProgressLabel({ name: s.name });
  return toolPastLabel({ name: s.name });
};

// Rewrites internal action labels into human copy. Tool steps fall through.
export const friendlyLabel = (s: TimelineStep): string => {
  if (s.id === 'action:context') {
    if (s.status === 'active') return 'Catching up on our chat';
    if (s.status === 'error') return STEP_CONTEXT_LOAD_FAILED;
    return 'Caught up';
  }
  if (s.id.startsWith('action:compose')) {
    if (s.status === 'active') return 'Thinking it through';
    if (s.status === 'error') return 'Hit a snag thinking';
    return 'Decided what to do next';
  }
  return rowText(s);
};

// Folds elapsed time into the active row's label, so a long step reads as
// alive rather than stalled. Keeps the REAL step label — it's the informative
// part — and appends the liveness cue.
export const softenedLabel = (
  base: string,
  elapsedSec: number,
  isTool: boolean
): string => {
  if (elapsedSec < SOFTEN_AT_S) return base;
  if (elapsedSec < WORRY_AT_S) {
    return isTool
      ? `${base} — ${workingStatusAt(elapsedSec).toLowerCase()}…`
      : workingStatusAt(elapsedSec);
  }
  return isTool
    ? `${base} — still going; deep research can take a couple of minutes`
    : 'Hmm, this is slow — you can keep waiting or send a new message';
};

// Live title: rotates through `claudeThinkingStates` while a step runs, else
// names the last step, so it's never blank and never echoes the row below.
export const streamingHeaderLabel = (
  visible: TimelineStep[],
  active: TimelineStep | undefined,
  elapsedSec: number
): string => {
  const base = active
    ? thinkingWordAt(elapsedSec)
    : contextualThinkingLabel(visible);
  return base.endsWith('...') ? base : `${base}...`;
};

const totalDurationMs = (steps: TimelineStep[]): number =>
  steps.reduce((sum, s) => sum + (s.durationMs ?? 0), 0);

// Collapsed title: "Thinking done. Thought for 3.5 seconds." Drops the timing
// when nothing reported a duration; surfaces a failure count (the list is
// collapsed, so the user wouldn't otherwise see it).
export const settledHeaderLabel = (visible: TimelineStep[]): string => {
  const errorCount = visible.filter((s) => s.status === 'error').length;
  const ms = totalDurationMs(visible);
  const timing =
    ms > 0 ? ` Thought for ${(ms / 1000).toFixed(1)} seconds.` : '';

  if (errorCount > 0) {
    return `Thinking done, with ${pluralise(errorCount, 'issue')}.${timing}`;
  }
  return `Thinking done.${timing}`;
};

export const formatTokens = (n: number): string =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

export const formatElapsed = (sec: number): string => {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

export const formatDuration = (ms: number): string =>
  ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

// Hides tool-error rows that don't represent a real user-visible failure.
//
//   - While streaming: hide ALL tool errors. Most are smart-tool rejections
//     the LLM retries within seconds; a "Failed" row alarms the user about a
//     transient that fixes itself.
//   - Post-stream: keep a tool error only if no later step of the same name
//     succeeded. A succeeded retry means the original was internal recovery.
//
// Action steps (`action:` prefix) mean routing/context/compose really failed,
// so they're never filtered here.
export const dropRetriedErrors = (
  steps: TimelineStep[],
  streaming: boolean
): TimelineStep[] => {
  const out: TimelineStep[] = [];
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (!s) continue;
    if (s.status === 'error' && !s.id.startsWith('action:')) {
      if (streaming) continue;
      const supersededByLater = steps
        .slice(i + 1)
        .some((later) => later.name === s.name && later.status !== 'error');
      if (supersededByLater) continue;
    }
    out.push(s);
  }
  return out;
};
